"use client"

// components/google-ads-tag.tsx
//
// Loads the Google Ads tag (gtag.js) — but ONLY after the visitor has stored
// marketing consent. Mounted once, site-wide, from app/layout.tsx.
//
// Read lib/analytics/google-ads.ts first: it carries the reasoning for why this
// is load-after-consent rather than the load-always-with-denied-defaults shape
// Google's own docs recommend. Short version: every Google tag was removed from
// this site on 2026-06-24 for loading before consent, and the campaign bids on
// Maximize Clicks where conversion volume does not drive spend.
//
// The component renders nothing. It watches the consent state and injects the
// script the first time marketing consent is present — on mount for a returning
// visitor who already accepted, or on the `cookie-consent-updated` event that
// cookie-consent.tsx dispatches the moment they accept. It never removes the
// script on withdrawal (a script cannot be un-run); instead consent is checked
// AGAIN at fire time in fireAdsConversion, so a withdrawal stops conversions
// even though the tag stays resident until the next page load.

import { useEffect, useState } from "react"
import Script from "next/script"

import { GOOGLE_ADS_ID, hasAdsConsent } from "@/lib/analytics/google-ads"

export default function GoogleAdsTag() {
  const [consented, setConsented] = useState(false)

  useEffect(() => {
    const sync = () => {
      // Latch: once loaded, stay loaded for this page. Unmounting <Script>
      // would not unload gtag anyway, and re-mounting it would re-run the tag.
      if (hasAdsConsent()) setConsented(true)
    }
    sync()
    window.addEventListener("cookie-consent-updated", sync)
    return () => window.removeEventListener("cookie-consent-updated", sync)
  }, [])

  if (!consented) return null

  return (
    <>
      {/*
        Consent Mode v2. The defaults are set BEFORE gtag.js is configured, in
        the same synchronous block, so Google never sees an unset state. They
        are written as denied-then-granted rather than granted outright because
        that is the correct shape if this component is ever switched to load
        unconditionally (delete the `consented` gate, keep everything else) —
        and because the explicit signals are what Google's modelling reads.
      */}
      <Script
        id="google-ads-gtag"
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`}
      />
      <Script id="google-ads-consent-default" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = window.gtag || gtag;
          gtag('consent', 'default', {
            'ad_storage': 'denied',
            'ad_user_data': 'denied',
            'ad_personalization': 'denied',
            'analytics_storage': 'denied'
          });
          gtag('consent', 'update', {
            'ad_storage': 'granted',
            'ad_user_data': 'granted',
            'ad_personalization': 'granted'
          });
          gtag('js', new Date());
          gtag('config', '${GOOGLE_ADS_ID}');
        `}
      </Script>
    </>
  )
}
